import { FC, PropsWithChildren, ReactNode } from 'react';
import { Document } from '@contentful/rich-text-types';
import Head from 'next/head';
import { ColumnWidth, ICard, ISectionWrapper, ITestable, ResponsiveProp, TEXT_NODES } from '@belong/types';
import { Card } from '../../forms/Card';
import { Section } from '../../styles';
import ButtonGroup from '../../styles/ButtonGroup';
import ContentFormatter from '../../styles/ContentFormatter';
import { Copy, Heading, RichText } from '../../styles/Typography';
import { Column, Columns } from '../../ui';
import { Center } from './styles';

interface IProps extends ITestable {
  pageTitle?: string;
  children?: ReactNode;
  cardProps?: Omit<ICard, 'children'>;
  includeSectionWrapper?: boolean;
  sectionBackground?: ISectionWrapper['hasBackground'];
  alignX?: 'left' | 'center' | 'right';
  width?: ResponsiveProp<ColumnWidth> | undefined;
}

export const Layout: FC<IProps> = ({
  children,
  pageTitle,
  cardProps,
  includeSectionWrapper,
  sectionBackground = 'grey',
  'data-testid': testid,
  alignX = 'center',
  width = { lg: '2/3', xl: '1/2' }
}: IProps) => {
  const layout = (
    <>
      {pageTitle && (
        <Head>
          <title>{pageTitle}</title>
        </Head>
      )}
      <Columns alignX={alignX}>
        <Column width={width}>
          <Card {...(!includeSectionWrapper && { 'data-testid': testid })} {...cardProps}>
            {children}
          </Card>
        </Column>
      </Columns>
    </>
  );

  // Conditionally render with or without Section wrapper
  return (
    <>
      {includeSectionWrapper ? (
        <Section hasPaddingTop hasPaddingBottom hasBackground={sectionBackground} data-testid={testid} containsCard>
          {layout}
        </Section>
      ) : (
        layout
      )}
    </>
  );
};

Layout.displayName = 'Layout';

export const Header = ({
  title,
  description,
  align = 'center',
  copySize
}: {
  title?: string;
  description?: string | Document;
  align?: 'left' | 'center' | 'right';
  copySize?: TEXT_NODES;
}) => {
  let theText: ReactNode = null;

  if (description) {
    if (typeof description === 'string') {
      theText = (
        <Copy variant="large" alignment={align}>
          {description}
        </Copy>
      );
    } else {
      theText = <RichText h2={TEXT_NODES.headingLarge} html={description} p={copySize} />;
    }
  }

  return (
    <ContentFormatter spacing="medium">
      {title && (
        <Heading alignment={align} as="h1" tabIndex={-1}>
          {title}
        </Heading>
      )}
      {theText && align === 'center' ? <Center>{theText}</Center> : theText}
    </ContentFormatter>
  );
};

Header.displayName = 'Header';

export const Footer: FC<PropsWithChildren> = ({ children, ...otherProps }) => (
  <ButtonGroup {...otherProps} hasControl={{ xs: ['column', 'center', false], md: ['row', 'right', true] }}>
    {children}
  </ButtonGroup>
);
Footer.displayName = 'Footer';
